/* Peak Saunas site-wide analytics -- schema pk-2.0, 2026-09-01
 * (v1 was 2026-07-29; see GA4_EVENT_TAXONOMY.md for the full taxonomy.)
 *
 * Loads AFTER snippet-peak-analytics.liquid, which has already resolved
 * window.PK.vid / .experiment / .variant before paint.
 *
 * WHY WE LOAD OUR OWN GTAG: peaksaunas.com serves GA4 only through Shopify's
 * Web Pixels sandbox, so window.gtag is undefined on ordinary storefront pages
 * (verified live 2026-07-29). We load our own tag with send_page_view:false --
 * the same pattern already working in production on /pages/landing-page-11.
 * The first-party _ga cookie lives on the main document origin, so this tag
 * shares a client_id with the sandbox pixel and our user properties attach to
 * Shopify's own add_to_cart / begin_checkout / purchase events too.
 *
 * EVENT NAMING: every event we emit is prefixed pk_. Shopify's pixel already
 * sends add_to_cart, view_item, begin_checkout and purchase -- re-sending those
 * names would silently double them. Never emit an unprefixed GA4 event here.
 *
 * WE NEVER SEND A CONVERSION. No purchase, no value, no currency, no AW- tag.
 * Peak double-counted revenue once; ad tracking is fixed and stays untouched.
 * Everything in this file is engagement measurement only.
 *
 * ---- what changed in pk-2.0 -------------------------------------------------
 *  * FIX: pk_template read every event as 'unknown' since 2026-07-29. It was
 *    derived from a `template-*` class on <body>, which the Aurora theme does
 *    not set (body class is "color-primary round-level-1 round-style").
 *    Now read from window.__st.p. This silently cost us page-type segmentation
 *    -- the single most useful cut for CRO -- on every event for five weeks.
 *  * Clarity is now driven from here: every tracked event is mirrored as a
 *    Clarity smart event and the variant/template/device are set as custom
 *    tags, so session recordings become filterable by our own funnel.
 *  * New surfaces instrumented: collection cards (with position), compare
 *    table, PDP gallery + FAQ, direct-to-checkout clicks, financing/HSA,
 *    chat, quiz, and our own rage/dead-click detection.
 *
 * ---- pk-2.2 (2026-10-01) ---------------------------------------------------
 *  * pk_theme_arm: which theme of the Oct 2026 split test rendered the page
 *    (austin | caleb | live), set by snippets/pk-theme-arm.liquid as
 *    PK.themeArm. Sent as a USER PROPERTY and as an EVENT PARAMETER on every
 *    event, and as a Clarity tag. Register both GA4 custom dimensions
 *    (event-scoped + user-scoped, key pk_theme_arm) or it reads (not set).
 *
 * ---- pk-2.1 (2026-09-19) ---------------------------------------------------
 *  * pk_nav_click: header + drawer menu link clicks (surface, pk_selector,
 *    position). Added so Learn-menu decisions use real clicks, not referrers.
 */
(function () {
  "use strict";

  var GA4_ID = 'G-Q4S7FKHHR8';
  var SCHEMA_VERSION = 'pk-2.2';
  var PK = (window.PK = window.PK || {});

  if (PK.loaded) return;            // snippet rendered twice; don't double-bind
  PK.loaded = true;

  // ---------------------------------------------------------------- consent
  // Shopify's privacy API decides whether analytics may run. When the API is
  // absent we proceed, matching what Shopify's own pixel and the existing
  // landing pages already do on this shop.
  function analyticsAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || typeof cp.analyticsProcessingAllowed !== 'function') return true;
      return cp.analyticsProcessingAllowed();
    } catch (err) { return true; }
  }

  // ------------------------------------------------------------------ gtag
  var gtagReady = false;
  var userPropsSet = false;

  // Runs whether or not WE created gtag. The landing pages (/pages/landing-
  // page-11 and friends) load their own tag, and an early return here would
  // mean the variant silently never attaches on exactly those pages.
  function setUserProps() {
    if (userPropsSet || typeof window.gtag !== 'function') return;
    userPropsSet = true;
    // User-scoped, so they also qualify events sent by the sandbox pixel --
    // including Shopify's own add_to_cart / begin_checkout / purchase.
    window.gtag('set', 'user_properties', {
      ab_variant: PK.variant || 'control',
      ab_experiment: PK.experiment || 'none',
      pk_vid: PK.vid || 'unknown',
      pk_theme_arm: PK.themeArm || 'unknown'   /* pk-2.2: split-test arm; user-scoped so Shopify's own purchase carries it */
    });
  }

  function ensureGtag() {
    if (gtagReady) { setUserProps(); return true; }
    if (typeof window.gtag === 'function') { gtagReady = true; setUserProps(); return true; }
    try {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function () { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
      // send_page_view:false -- page views stay owned by Shopify's pixel.
      window.gtag('config', GA4_ID, { send_page_view: false, transport_type: 'beacon' });
      setUserProps();
      var load = function () {
        var s = document.createElement('script');
        s.async = true;
        s.src = 'https://www.googletagmanager.com/gtag/js?id=' + GA4_ID;
        document.head.appendChild(s);
      };
      // Deferred to idle so site-wide instrumentation costs nothing against
      // LCP. Events queue into dataLayer meanwhile and flush on arrival.
      if (window.requestIdleCallback) window.requestIdleCallback(load, { timeout: 4000 });
      else setTimeout(load, 2000);
      gtagReady = true;
      return true;
    } catch (err) { return false; }
  }

  // --------------------------------------------------------------- template
  // window.__st.p is Shopify's own page-type marker and is present on every
  // storefront page ('product', 'collection', 'index', 'page', 'cart', ...).
  // The v1 body-class approach returned 'unknown' everywhere -- see header.
  function pageTemplate() {
    try {
      if (window.__st && window.__st.p) return String(window.__st.p);
      var m = document.body.className.match(/template-(\S+)/);
      if (m) return m[1];
      var p = location.pathname;
      if (p === '/') return 'index';
      if (/^\/products\//.test(p)) return 'product';
      if (/^\/collections\//.test(p)) return 'collection';
      if (/^\/pages\//.test(p)) return 'page';
      if (/^\/cart/.test(p)) return 'cart';
      return 'other';
    } catch (err) { return 'unknown'; }
  }
  var TEMPLATE = pageTemplate();

  // ---------------------------------------------------------------- clarity
  // Clarity is installed sitewide (project s0ni49iryy) but its loader sits
  // inside a requestIdleCallback, so window.clarity does not exist for the
  // first seconds of a page. Queue locally and flush when it appears, or give
  // up quietly -- Clarity is a nice-to-have and must never hold up GA4.
  //
  // WHY MIRROR AT ALL: GA4 tells us the RATE ("18% open the warranty row").
  // Clarity tells us WHY, but only if we can find those sessions. Tagging the
  // session with our own events makes the recordings filterable by our funnel.
  var clarityQ = [];
  var clarityTries = 0;
  function clarityReady() { return typeof window.clarity === 'function'; }
  function flushClarity() {
    if (!clarityReady()) return false;
    while (clarityQ.length) {
      var c = clarityQ.shift();
      try { window.clarity.apply(null, c); } catch (err) { /* no-op */ }
    }
    return true;
  }
  function clarityCall() {
    var args = Array.prototype.slice.call(arguments);
    if (clarityReady()) { try { window.clarity.apply(null, args); } catch (err) {} return; }
    if (clarityQ.length < 60) clarityQ.push(args);   // bounded: never grow unbounded
  }
  // Poll briefly for the lazy loader, then stop. 20 x 500ms = 10s, which
  // comfortably covers the 2s setTimeout fallback in theme.liquid.
  var clarityPoll = setInterval(function () {
    if (flushClarity() || ++clarityTries > 20) clearInterval(clarityPoll);
  }, 500);

  // Custom tags: session-level dimensions we can filter recordings by.
  // Clarity allows 128 custom tags per project; we use three.
  function tagClarity() {
    clarityCall('set', 'ab_variant', String(PK.variant || 'control'));
    clarityCall('set', 'pk_template', TEMPLATE);
    clarityCall('set', 'pk_device', deviceClass());
    clarityCall('set', 'pk_theme_arm', String(PK.themeArm || 'unknown'));   /* pk-2.2: recordings filterable by arm */
  }

  // Only a curated subset is mirrored. Clarity already records scrolling and
  // page views natively, so mirroring those would be noise; what earns a
  // filter is intent and friction.
  var CLARITY_MIRROR = /^pk_(pdp_|bb_|fin_|lead|quiz_|rage_click|dead_click|atc_)/;

  function deviceClass() {
    try { return window.matchMedia('(max-width: 768px)').matches ? 'mobile' : 'desktop'; }
    catch (err) { return 'unknown'; }
  }

  // ----------------------------------------------------------------- track
  function track(name, params) {
    try {
      if (!analyticsAllowed() || !ensureGtag()) return;
      var p = params || {};
      p.schema_version = SCHEMA_VERSION;
      p.ab_variant = PK.variant || 'control';
      p.ab_experiment = PK.experiment || 'none';
      p.pk_device = deviceClass();
      p.pk_template = TEMPLATE;
      p.pk_theme_arm = PK.themeArm || 'unknown';   /* pk-2.2: split-test arm on every event */
      if (!p.pk_path) p.pk_path = location.pathname.slice(0, 100);
      window.gtag('event', name, p);
      if (CLARITY_MIRROR.test(name)) clarityCall('event', name);
    } catch (err) { /* analytics must never break the storefront */ }
  }

  var firedOnce = {};
  function trackOnce(name, params) {
    if (firedOnce[name]) return;
    firedOnce[name] = true;
    track(name, params);
  }

  // Identifiers other scripts hand to the backend so a lead can later be
  // joined to the variant that produced it. GA4's client_id is the second half
  // of the _ga cookie value (GA1.1.<client_id>.<first_seen>).
  function gaClientId() {
    var m = document.cookie.match(/_ga=GA\d\.\d\.(\d+\.\d+)/);
    return m ? m[1] : null;
  }
  PK.ids = function () {
    return {
      pk_vid: PK.vid || null,
      ga_client_id: gaClientId(),
      ab_experiment: PK.experiment || 'none',
      ab_variant: PK.variant || 'control',
      pk_theme_arm: PK.themeArm || null   /* pk-2.2 */
    };
  };

  PK.track = track;
  PK.trackOnce = trackOnce;
  (PK.queue || []).forEach(function (q) { track(q[0], q[1]); });
  PK.queue = [];

  tagClarity();

  // ------------------------------------------------- page view (our own)
  // Shopify's pixel owns GA4's page_view. This is a parallel, variant-stamped
  // denominator we control, so every rate below is computable without relying
  // on session stitching between the sandbox tag and this one.
  track('pk_page_view', { pk_path: location.pathname.slice(0, 100) });

  // ------------------------------------------------------------- helpers
  // A short, stable, PII-free description of an element. Never innerText --
  // that could carry something a customer typed.
  function selectorOf(el) {
    try {
      if (!el || !el.tagName) return 'unknown';
      var s = el.tagName.toLowerCase();
      if (el.id) return (s + '#' + el.id).slice(0, 60);
      var cls = (el.className && el.className.toString ? el.className.toString() : '')
                  .trim().split(/\s+/).slice(0, 2).join('.');
      if (cls) s += '.' + cls;
      return s.slice(0, 60);
    } catch (err) { return 'unknown'; }
  }
  function handleFromPath(p) {
    var m = (p || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1].slice(0, 80) : '';
  }
  function currentCollection() {
    var m = location.pathname.match(/\/collections\/([^/?#]+)/);
    return m ? m[1].slice(0, 60) : '';
  }

  // --------------------------------------------------- lead-surface intents
  // One delegated listener, so it survives sections re-rendering and costs a
  // single handler rather than one per element.
  // Matched loosely on purpose. The site currently links the quiz at BOTH
  // /pages/30-second-sauna-selector-quiz and /pages/sauna-selector-quiz (the
  // latter 404s as of 2026-07-29), and an exact path would have silently
  // missed most quiz clicks. Prefer under-specific here: a false positive is
  // one mislabelled intent, a false negative is a metric that reads zero.
  var INTENTS = [
    { type: 'quiz',      test: function (a) { return /quiz/.test(a.pathname); } },
    { type: 'financing', test: function (a) { return /financ/.test(a.pathname); } },
    { type: 'contact',   test: function (a) { return /\/contact/.test(a.pathname); } },
    { type: 'phone',     test: function (a) { return a.protocol === 'tel:'; } },
    { type: 'email',     test: function (a) { return a.protocol === 'mailto:'; } }
  ];

  // Position of a product link among all product links on the page. Answers
  // "is the first row carrying everything?" without needing per-card markup,
  // which the collection template does not have.
  function productPosition(el) {
    try {
      var links = document.querySelectorAll('a[href*="/products/"]');
      for (var i = 0; i < links.length; i++) if (links[i] === el) return i + 1;
    } catch (err) { /* no-op */ }
    return 0;
  }

  document.addEventListener('click', function (e) {
    try {
      var el = e.target && e.target.closest ? e.target.closest('a,button') : null;
      if (!el) return;

      if (el.tagName === 'A' && el.href) {
        for (var i = 0; i < INTENTS.length; i++) {
          if (INTENTS[i].test(el)) {
            track('pk_lead_intent', { intent_type: INTENTS[i].type });
            return;
          }
        }
        if (/\/products\//.test(el.pathname)) {
          // A click inside the compare table is reported by pk_coll_compare_click.
          // Without this, one click would send two events for the same action.
          if (el.closest('.pk-cmp__table,.pkx-compare')) return;
          // Kept as pk_product_click (live since July, has history) rather than
          // renamed -- it just carries position and collection context now.
          track('pk_product_click', {
            pk_handle: handleFromPath(el.pathname),
            position: productPosition(el),
            coll_handle: currentCollection()
          });
          return;
        }
        if (/\/collections\//.test(el.pathname)) {
          track('pk_collection_click', {
            coll_handle: (el.pathname.match(/\/collections\/([^/?#]+)/) || ['', ''])[1].slice(0, 60)
          });
          return;
        }
      }

      // Add-to-cart intent. Shopify's pixel still owns the authoritative
      // add_to_cart; this is the CLICK, which also captures failures.
      if (el.closest('form[action*="/cart/add"]') &&
          (el.type === 'submit' || /add to cart|buy|purchase/i.test(el.textContent || ''))) {
        lastAtcClick = Date.now();
        track('pk_atc_click', {});
      }
    } catch (err) { /* never break a click */ }
  }, true);

  // Catches programmatic and Enter-key submits the click handler would miss.
  // A button click that submits its form fires BOTH handlers, so the click
  // wins and this one stands down -- one user action, one event.
  var lastAtcClick = 0;
  document.addEventListener('submit', function (e) {
    try {
      var f = e.target;
      if (!f || !f.action || !/\/cart\/add/.test(f.action)) return;
      if (Date.now() - lastAtcClick < 1000) return;
      track('pk_atc_submit', {});
    } catch (err) { /* no-op */ }
  }, true);

  // ------------------------------------------------------ header navigation
  // pk-2.1 (2026-09-19): which menu links people actually click. Before this
  // the only proxy was GA4 pageReferrer, which cannot tell a header click from
  // an in-body link. Covers the desktop header (top items + dropdowns) and the
  // mobile drawer. Parent toggles (href="#") are skipped: only real links.
  //   surface     'header:learn' | 'drawer:learn' | 'header:top' | 'drawer:top' ...
  //   pk_selector the link's href (path only for internal links)
  //   position    1-based index among its sibling links
  // All three params are already registered GA4 dimensions -- no new custom
  // definitions needed. Not mirrored to Clarity (navigation is not friction).
  document.addEventListener('click', function (e) {
    try {
      var el = e.target && e.target.closest
        ? e.target.closest('a.header__item,a.header__menu-item,a.drawer-menu__menu-item,a.drawer-menu__list-title-label')
        : null;
      if (!el) return;
      var href = el.getAttribute('href') || '';
      if (!href || href === '#') return;
      var cls = el.className || '';
      var where = /drawer-menu/.test(cls) ? 'drawer' : 'header';
      var group = 'top';
      if (/header__menu-item/.test(cls)) {
        var fe = el.closest('[id^="HeaderDropdownMenu-"]');
        group = fe ? fe.id.replace('HeaderDropdownMenu-', '') : 'dropdown';
      } else if (/drawer-menu__menu-item/.test(cls)) {
        var list = el.closest('.drawer-menu__list');
        var lbl = list ? list.querySelector('.drawer-menu__list-title-label') : null;
        group = lbl ? (lbl.getAttribute('menu-title') || lbl.textContent || 'submenu') : 'submenu';
      }
      group = String(group).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30);
      var sel = href;
      try { if (el.hostname === location.hostname) sel = el.pathname; } catch (err2) { /* keep href */ }
      var pos = 0;
      try {
        var sib = el.parentElement ? el.parentElement.querySelectorAll('a') : [];
        for (var i = 0; i < sib.length; i++) if (sib[i] === el) { pos = i + 1; break; }
      } catch (err3) { /* no-op */ }
      track('pk_nav_click', { surface: where + ':' + group, pk_selector: sel.slice(0, 60), position: pos });
    } catch (err) { /* never break a click */ }
  }, true);

  // ------------------------------------------- direct-to-checkout intent
  // The buy box bypasses the cart and sends buyers straight to /checkout.
  // Bound HERE rather than in pk-bb-after.liquid because that snippet renders
  // twice on a PDP; this file is guarded by PK.loaded and binds exactly once.
  // This is the CLICK. Shopify's pixel still owns begin_checkout -- we must
  // never emit that name ourselves.
  document.addEventListener('click', function (e) {
    try {
      var b = e.target && e.target.closest
        ? e.target.closest('[id^="product-buy-btn-"],[id^="sticky-product-buy-btn-"],[data-pk-pay-checkout]')
        : null;
      if (!b || b.disabled) return;
      var activeTab = document.querySelector('[data-pk-tab][aria-selected="true"]');
      track('pk_bb_checkout_click', {
        pay_method: activeTab ? activeTab.getAttribute('data-pk-tab') : 'full',
        pk_handle: handleFromPath(location.pathname),
        is_sticky: /^sticky-/.test(b.id || '') ? 'yes' : 'no'
      });
    } catch (err) { /* no-op */ }
  }, true);

  // The sticky buy bar sets data-atc-visible on <body>. Firing once when it
  // first appears tells us whether buyers ever reach the state the sticky bar
  // is designed for.
  try {
    if (window.MutationObserver) {
      var stickyObs = new MutationObserver(function () {
        if (document.body.getAttribute('data-atc-visible') === 'true') {
          trackOnce('pk_bb_sticky_shown', {});
          stickyObs.disconnect();
        }
      });
      stickyObs.observe(document.body, { attributes: true, attributeFilter: ['data-atc-visible'] });
    }
  } catch (err) { /* no-op */ }

  // --------------------------------------------------- financing / HSA
  // The three pay tabs are tracked in pk-bb-after.liquid (pk_pdp_pay_tab).
  // These are the deeper clicks: opening the provider's own explainer.
  document.addEventListener('click', function (e) {
    try {
      var el = e.target && e.target.closest ? e.target.closest('[data-pk-modal],a,button') : null;
      if (!el) return;
      var modal = el.getAttribute && el.getAttribute('data-pk-modal');
      if (modal === 'pk-m-bread') track('pk_fin_modal_open', { provider: 'bread' });
      else if (modal === 'pk-m-truemed') track('pk_fin_modal_open', { provider: 'truemed' });
      else if (el.href && /truemed/i.test(el.href)) track('pk_fin_learn_click', { provider: 'truemed' });
      else if (el.href && /(affirm|bread|katapult|financ)/i.test(el.href)) track('pk_fin_learn_click', { provider: 'bread' });
    } catch (err) { /* no-op */ }
  }, true);

  // ------------------------------------------------------ PDP: gallery + FAQ
  if (TEMPLATE === 'product') {
    // Do buyers look past the first photo? Reported as a bucketed index so the
    // dimension stays low-cardinality.
    document.addEventListener('click', function (e) {
      try {
        var t = e.target && e.target.closest
          ? e.target.closest('[data-media-id],.product-gallery__media,.product__media-item,.product-gallery__thumbnail')
          : null;
        if (!t) return;
        var all = document.querySelectorAll('[data-media-id],.product-gallery__media,.product__media-item');
        var idx = 0;
        for (var i = 0; i < all.length; i++) if (all[i] === t || all[i].contains(t)) { idx = i + 1; break; }
        track('pk_pdp_media_view', { media_index: idx > 10 ? '10+' : String(idx) });
      } catch (err) { /* no-op */ }
    }, true);

    // FAQ rows live outside .product-details__container, so the accordion
    // handler in pk-bb-after.liquid deliberately ignores them. This picks them
    // up. The question text is the label -- it is our own copy, never a user's.
    document.addEventListener('click', function (e) {
      try {
        var btn = e.target && e.target.closest ? e.target.closest('summary,accordeon-button,[class*="faq"] button') : null;
        if (!btn) return;
        if (btn.closest('.product-details__container')) return;   // owned by pk-bb-after
        var wrap = btn.closest('[class*="faq"],.pkx-faq');
        if (!wrap) return;
        track('pk_pdp_faq_open', { faq_question: (btn.innerText || '').trim().split('\n')[0].slice(0, 80) });
      } catch (err) { /* no-op */ }
    }, true);
  }

  // ------------------------------------------------- collection: compare table
  if (TEMPLATE === 'collection') {
    // Deferred so sections that render late are in the DOM before we look.
    setTimeout(function () {
      try {
        var cmp = document.querySelector('.pk-cmp__table,.pkx-compare,[class*="compare"] table,table');
        if (!cmp) return;
        var target = cmp.closest('.pk-cmp__scroll,.custom-liquid,section') || cmp;

        // Deliberately NOT IntersectionObserver. The compare table lives in a
        // horizontal scroll wrapper, so its box can measure zero width, and an
        // area-based observer never reports an intersection for a zero-area box.
        // A vertical-overlap test against the viewport is immune to that.
        var seen = false;
        function checkCompare() {
          if (seen) return;
          var r = target.getBoundingClientRect();
          var vh = window.innerHeight || document.documentElement.clientHeight || 0;
          if (vh > 0 && r.top < vh && r.bottom > 0) {
            seen = true;
            window.removeEventListener('scroll', onCompareScroll);
            trackOnce('pk_coll_compare_view', { coll_handle: currentCollection() });
          }
        }
        var compareTick = false;
        function onCompareScroll() {
          if (compareTick) return;
          compareTick = true;
          window.requestAnimationFrame(function () { compareTick = false; checkCompare(); });
        }
        window.addEventListener('scroll', onCompareScroll, { passive: true });
        checkCompare();   // already in view on load

        cmp.addEventListener('click', function (e) {
          var a = e.target && e.target.closest ? e.target.closest('a[href*="/products/"]') : null;
          if (!a) return;
          track('pk_coll_compare_click', { pk_handle: handleFromPath(a.pathname) });
        }, true);
      } catch (err) { /* no-op */ }
    }, 1200);
  }

  // ------------------------------------------------------------------ chat
  // Gorgias mounts late, so watch for its container rather than binding to it.
  document.addEventListener('click', function (e) {
    try {
      var el = e.target && e.target.closest
        ? e.target.closest('#gorgias-chat-container,[class*="gorgias"],[id*="chat-button"],[class*="chat-widget"]')
        : null;
      if (el) trackOnce('pk_chat_open', {});
    } catch (err) { /* no-op */ }
  }, true);

  // ------------------------------------------------------------------ quiz
  // Octane AI. Its own GA4 events are unprefixed and outside our control; these
  // are ours, so the quiz sits on the same funnel as everything else.
  if (/quiz/.test(location.pathname)) {
    trackOnce('pk_quiz_start', {});
    var lastStep = 0;
    document.addEventListener('click', function (e) {
      try {
        var el = e.target && e.target.closest ? e.target.closest('button,a') : null;
        if (!el) return;
        var txt = (el.innerText || '').trim().toLowerCase();
        if (/^(next|continue|start)/.test(txt)) {
          lastStep++;
          track('pk_quiz_step', { step_index: String(lastStep) });
        }
        if (el.href && /\/products\//.test(el.pathname)) {
          trackOnce('pk_quiz_complete', { result_handle: handleFromPath(el.pathname) });
        }
      } catch (err) { /* no-op */ }
    }, true);
  }

  // ------------------------------------------------------------ engagement
  var maxScroll = 0;
  function scrollPct() {
    var h = document.documentElement;
    var denom = (h.scrollHeight - h.clientHeight);
    if (denom <= 0) return 100;
    return Math.min(100, Math.round(((window.scrollY || h.scrollTop) / denom) * 100));
  }
  function checkScrollDepth() {
    var p = scrollPct();
    if (p > maxScroll) maxScroll = p;
    if (maxScroll >= 50) trackOnce('pk_scroll_50', {});
    if (maxScroll >= 90) trackOnce('pk_scroll_90', {});
  }
  var scrollTick = false;
  window.addEventListener('scroll', function () {
    if (scrollTick) return;
    scrollTick = true;
    window.requestAnimationFrame(function () {
      scrollTick = false;
      checkScrollDepth();
    });
  }, { passive: true });

  // A page that fits on screen never fires a scroll event, so without this it
  // would report no scroll depth at all even though the visitor saw all of it.
  // The 100px allowance covers pages that technically overflow by a hairline
  // but are functionally fully visible. Deferred so late-loading sections are
  // counted in scrollHeight before we decide.
  setTimeout(function () {
    var h = document.documentElement;
    if (h.scrollHeight - h.clientHeight < 100) { maxScroll = 100; checkScrollDepth(); }
  }, 1200);

  // Active time only -- a backgrounded tab stops accruing, so "engaged" means
  // engaged rather than "left open in another window".
  var activeMs = 0, lastTick = Date.now();
  setInterval(function () {
    var now = Date.now();
    if (!document.hidden) activeMs += now - lastTick;
    lastTick = now;
    if (activeMs >= 30000) trackOnce('pk_engaged_30s', {});
    if (activeMs >= 120000) trackOnce('pk_engaged_120s', {});
  }, 1000);

  // ------------------------------------------------------------- friction
  // Our own rage/dead-click detection. Clarity measures the same two things
  // independently; when both agree on a page, that is a finding worth acting
  // on. Doing it here as well is what makes friction joinable to ab_variant
  // and to the rest of the funnel inside GA4, which Clarity cannot do.
  //
  // Both are capped per page view. A genuinely broken element could otherwise
  // send hundreds of events from one frustrated visitor and skew the counts
  // toward whoever was angriest rather than how many people hit it.
  // Count only modals that are actually ON SCREEN. This PDP keeps six
  // [aria-modal="true"] nodes in the DOM at all times, all hidden, so a plain
  // querySelector for one always matched and silently disabled dead-click
  // detection entirely (found in live testing 2026-09-01).
  function visibleModals() {
    try {
      var els = document.querySelectorAll('.pk-mscrim.pk-open,[aria-modal="true"],dialog[open]');
      var n = 0;
      for (var i = 0; i < els.length; i++) {
        if (els[i].tagName === 'DIALOG' ? els[i].open : els[i].offsetParent !== null) n++;
      }
      return n;
    } catch (err) { return 0; }
  }

  var RAGE_CAP = 3, DEAD_CAP = 3;
  var rageSent = 0, deadSent = 0;
  var recent = [];

  document.addEventListener('click', function (e) {
    try {
      var now = Date.now();
      recent.push({ t: now, x: e.clientX, y: e.clientY, el: e.target });
      recent = recent.filter(function (r) { return now - r.t < 1000; });

      // Rage: 4+ clicks inside one second within a 30px radius.
      if (rageSent < RAGE_CAP && recent.length >= 4) {
        var first = recent[0], tight = true;
        for (var i = 1; i < recent.length; i++) {
          if (Math.abs(recent[i].x - first.x) > 30 || Math.abs(recent[i].y - first.y) > 30) { tight = false; break; }
        }
        if (tight) {
          rageSent++;
          recent = [];
          track('pk_rage_click', { pk_selector: selectorOf(e.target) });
          return;
        }
      }

      // Dead: a click on something non-interactive that changes nothing.
      if (deadSent >= DEAD_CAP) return;
      var el = e.target;
      if (!el || !el.closest) return;
      // 2026-10-05 (Caleb arm): the theme's own controls are custom elements or script-driven slides,
      // so taps on them counted as dead even though they work (gallery opener, its close/arrows,
      // thumbnail strip, videos). The gallery is position:fixed, so visibleModals() never saw it open.
      if (el.closest('a,button,input,select,textarea,label,summary,[role="button"],[onclick],accordeon-button,' +
          'product-modal-button,modal-button,carousel-button,carousel-dots .carousel__slide,[data-carousel-dot],video')) return;
      var beforeH = document.body.scrollHeight;
      var beforeUrl = location.href;
      var beforeModals = visibleModals();
      setTimeout(function () {
        try {
          if (location.href !== beforeUrl) return;                 // navigated
          if (document.body.scrollHeight !== beforeH) return;      // something expanded
          if (visibleModals() !== beforeModals) return;            // a modal opened
          deadSent++;
          track('pk_dead_click', { pk_selector: selectorOf(el) });
        } catch (err) { /* no-op */ }
      }, 500);
    } catch (err) { /* never break a click */ }
  }, true);

  // Exit snapshot. pagehide is the reliable one on iOS; visibilitychange
  // covers tab switches that never fire pagehide.
  var exitSent = false;
  function sendExit() {
    if (exitSent) return;
    exitSent = true;
    track('pk_page_exit', {
      active_seconds: Math.round(activeMs / 1000),
      max_scroll: maxScroll
    });
  }
  window.addEventListener('pagehide', sendExit);
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) sendExit();
  });

  // ---------------------------------------------------------- public lead
  // Called by the widget, the quiz, or any future form the moment a visitor
  // hands over identifying information. This is the hard conversion.
  PK.lead = function (leadType, extra) {
    var p = extra || {};
    p.lead_type = leadType;
    track('pk_lead', p);
  };

  // ------------------------------------------------- Northbeam email capture
  // Northbeam attributes a sign-up ONLY if fireEmailCaptureEvent runs with a
  // real email. Two facts, both read out of the live pixel on 2026-09-01
  // (ota-sp/714e0444-...js), not from their docs:
  //   1. The pixel binds NOTHING itself -- no submit listener, no form/input
  //      sniffing anywhere in the bundle. Their docs claim it covers "common
  //      areas (ex. footer)"; it does not. Every surface must call in.
  //   2. fireEmailCaptureEvent(email, location) opens with `if (email)`. The
  //      empty strings their docs say you may pass fail that check, log a
  //      console error and never fire. A real address is mandatory.
  // Consequence of getting this wrong: sign-ups land in Unattributed, so no
  // channel ever gets credit for the email it bought.
  //
  // This is measurement only -- it sends no revenue and no conversion value,
  // and never touches the ad pixels.
  var nbQueue = [], nbSent = {}, nbTries = 0;

  function nbFlush() {
    try {
      var NB = window.Northbeam;
      if (!NB || typeof NB.fireEmailCaptureEvent !== 'function') return false;
      while (nbQueue.length) {
        var job = nbQueue.shift();
        NB.fireEmailCaptureEvent(job[0], job[1]);
      }
      return true;
    } catch (err) { return true; }   // pixel present but throwing; stop retrying
  }

  // The pixel is async, so a fast submit can beat it. Hold the event and drain
  // once it lands. Give up after ~20s rather than poll forever.
  function nbFire(email, location) {
    try {
      email = (email || '').trim().toLowerCase();
      if (!email || email.indexOf('@') < 1) return;   // pixel would reject it
      var key = email + '|' + location;
      if (nbSent[key]) return;                        // multi-step forms re-fire
      nbSent[key] = true;
      nbQueue.push([email, location]);
      if (nbFlush()) return;
      var t = setInterval(function () {
        if (nbFlush() || ++nbTries > 40) clearInterval(t);
      }, 500);
    } catch (err) { /* never break a signup */ }
  }
  PK.emailCapture = nbFire;   // so the quiz/widget can report their own captures

  // Klaviyo popups and embeds render outside our markup, so the only reliable
  // hook is their onsite event. stepSubmit covers multi-step forms, where the
  // email is taken on an early step; nbSent dedupes the repeats.
  window.addEventListener('klaviyoForms', function (e) {
    try {
      var d = e && e.detail; if (!d) return;
      if (d.type !== 'submit' && d.type !== 'stepSubmit') return;
      var md = d.metaData || {};
      nbFire(md.$email || md.email || md.Email || '',
             'klaviyo_' + (d.formId || 'form'));
    } catch (err) { /* no-op */ }
  });

  // The footer/native Shopify newsletter form posts to /contact and reloads, so
  // read the address off the form at submit time -- after navigation it is gone.
  document.addEventListener('submit', function (e) {
    try {
      var f = e.target;
      if (!f || !f.action || !/\/contact/.test(f.action)) return;
      var input = f.querySelector('input[name="contact[email]"], input[type="email"]');
      if (!input || !input.value) return;
      nbFire(input.value, 'shopify_newsletter');
    } catch (err) { /* never break a signup */ }
  }, true);
})();
